import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoIcon, StopIcon } from './icons';
import ResidencyShell from './ResidencyShell';

interface ResidencyBlockedProps {
  /**
   * True only when this mount is the direct result of the candidate tapping
   * Continue on the question screen (the URL doesn't change, so nothing else
   * moves focus here — without it, a screen-reader user is left focused on a
   * button that no longer exists, with nothing announcing the block).
   *
   * False when the screen mounts already-blocked — a reload or
   * back-navigation with the answer already in sessionStorage. Nothing just
   * happened, so grabbing focus is unwarranted, and on desktop it would race
   * `DesktopBlock`'s own mount-focus of its Ok button, landing focus on this
   * heading behind the modal overlay and breaking that dialog's containment.
   */
  focusHeadingOnMount: boolean;
}

/**
 * The dead end for candidates who declare California or Illinois residency.
 * Deliberately has no footer button: there is nowhere to go from here, and the
 * `RequireResidency` guard keeps /questions and /answers closed.
 */
export default function ResidencyBlocked({
  focusHeadingOnMount,
}: ResidencyBlockedProps) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusHeadingOnMount) {
      headingRef.current?.focus();
    }
    // Intentionally mount-only: this fires once per mount using the prop
    // value captured at that time, which is exactly the transition-vs-arrival
    // distinction we want — see `focusHeadingOnMount` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResidencyShell title={t('residency.blocked_title')}>
      <div className="flex w-full max-w-[336px] flex-col items-center text-center">
        <div className="flex size-[68px] items-center justify-center rounded-[20px] bg-[#fbeaea] text-[#c0392b]">
          <StopIcon />
        </div>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 text-[21px] font-extrabold leading-[1.2] tracking-[-0.4px] text-[#16171a]"
        >
          {t('residency.blocked_heading')}
        </h1>

        <p className="mt-3 text-[14.5px] leading-[1.55] text-[#6b7079]">
          <Trans
            i18nKey="residency.blocked_description"
            components={{ b: <b className="font-bold text-[#16171a]" /> }}
          />
        </p>

        <div className="mt-5 flex items-center gap-2.5 rounded-[13px] border border-[#e6e8eb] bg-white px-[15px] py-[13px] text-left">
          <span className="shrink-0 text-[#8a8f98]">
            <InfoIcon />
          </span>
          <p className="text-[13.5px] leading-[1.4] text-[#3b3f46]">
            {t('residency.blocked_note')}
          </p>
        </div>
      </div>
    </ResidencyShell>
  );
}
